/**
 * Summary1Installation.jsx  (was SummaryProcesses.jsx)
 * Exact replica of Excel "Summary_Processes" sheet UI
 * Pulls data live from FormA, FormB, FormC, FormD, FormE, FormF contexts
 *
 * FIXES applied:
 * 1. Section 1(c): reads goodsCategory (new) with fallback to linkedProcessId
 * 2. Section 2 SEE table: passes formEPrecursors to D.calculateSEE() → full SEE
 * 3. Section 2 SEE table: SE(direct) ≠ SEE(direct) — separate fields used correctly
 * 4. Section 2 SEE table: embedded electricity = process elec + precursor elec contribution
 * 5. Section 3 detailed overview: precursor sub-rows read from FormE consumedInProcesses
 *    (not consumedOtherProcesses which is a FormD-internal field)
 * 6. Section 3 detailed overview: all SEE/EmbEm values use FULL (process+precursor) layer
 */

import React, { useMemo } from "react";
import { useFormContext } from "../context/FormContext";
import { D as DFormulas } from "../utils/CbamFormulas";
import exportSummaryExcel from "../utils/exportSummaryExcel";

// ─── Colour palette ───────────────────────────────────────────────────────────
const S = `
  @import url('https://fonts.googleapis.com/css2?family=Calibri:wght@400;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  .sp { font-family: Calibri, 'Trebuchet MS', Arial, sans-serif; font-size: 11px; color: #000; background: #fff; min-height: 100vh; }
  .sp-nav { background: #D8D8D8; padding: 4px 10px; display: flex; align-items: center; gap: 16px; border-bottom: 1px solid #b0b0b0; font-size: 10px; font-weight: 700; }
  .sp-nav-title { font-size: 12px; font-weight: 700; flex: 1; }
  .sp-nav-link { background: #fff; border: 1px solid #999; padding: 1px 8px; cursor: pointer; font-size: 10px; color: #000080; text-decoration: underline; }
  .sp-band { background: #376366; color: #E5F0F1; font-weight: 700; font-size: 11px; padding: 4px 8px; margin-top: 2px; }
  .sp-sub { color: #254244; font-weight: 700; font-size: 11px; padding: 3px 8px 2px; border-bottom: 1px solid #ccc; margin-bottom: 2px; }
  .sp table { border-collapse: collapse; font-size: 11px; width: 100%; }
  .sp td, .sp th { border: 1px solid #b0b0b0; padding: 2px 5px; white-space: nowrap; vertical-align: middle; min-height: 18px; line-height: 18px; }
  .c-hdr   { background: #376366; color: #E5F0F1; font-weight: 700; }
  .c-shdr  { background: #4d8285; color: #fff; font-weight: 700; }
  .c-label { background: #f2f2f2; }
  .c-green { background: #CCFFCC; }
  .c-yellow{ background: #FFFF00; }
  .c-white { background: #fff; }
  .c-grey  { background: #d9d9d9; color: #555; }
  .c-total { background: #D9E1F2; font-weight: 700; }
  .c-note  { background: #FFF2CC; font-style: italic; font-size: 10px; color: #555; }
  .c-id    { background: #E5F0F1; font-weight: 700; color: #376366; text-align: center; }
  .c-num   { text-align: right; }
  .c-center{ text-align: center; }
  .sp-inst-table td:first-child { min-width: 240px; text-align: right; padding-right: 8px; }
  .sp-section { margin: 0 0 4px; padding: 0; }
  .sp-body { padding: 6px 8px; overflow-x: auto; }
  .sp-abbr-table td:first-child { min-width: 220px; font-weight: 700; color: #254244; }
  .sp-abbr-table td:last-child  { color: #444; max-width: 600px; white-space: normal; }
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (v, decimals = 4) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return "—";
  return n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: decimals });
};
const fmtPct  = (v) => { const n = Number(v); if (!Number.isFinite(n)) return "—"; return (n * 100).toFixed(2) + "%"; };
const fmtDate = (v) => { if (!v) return "—"; try { return new Date(v).toLocaleDateString("en-GB"); } catch { return String(v); } };

// ─── Component ────────────────────────────────────────────────────────────────
const Summary1Installation = () => {
  const { allFormData } = useFormContext();

  const fA = allFormData?.formA || {};
  const fB = allFormData?.formB || {};
  const fC = allFormData?.formC || {};
  const fD = allFormData?.formD || {};
  const fE = allFormData?.formE || {};
  const fF = allFormData?.formF || {};

  // ── FIX 1: FormE precursors — needed for full SEE in calculateSEE() ────────
  const formEPrecursors = fE?.precursors || [];

  // ── Manufacturing Facility ──────────────────────────────────────────────────────
  const inst = {
    name:        fA.installationNameEnglish || fA.installationName || "—",
    street:      fA.street          || "—",
    economic:    fA.economicActivity || "—",
    city:        fA.city            || "—",
    country:     fA.country         || "—",
    unlocode:    fA.unLocode        || "—",
    lat:         fA.latitude        || "—",
    lon:         fA.longitude       || "—",
    periodStart: fmtDate(fA.reportingPeriodStart),
    periodEnd:   fmtDate(fA.reportingPeriodEnd),
    carbonPrice: fA.carbonPriceInstrument || fA.carbonPrice || "—",
    addlInfo:    fA.additionalInfo  || "—",
  };

  const aggregatedGoods  = fA.aggregatedGoods    || [];
  const formAProcesses   = fA.relevantProcesses  || [];
  const formAPrecursors  = fA.purchasedPrecursors || [];

  // ── Form C totals ──────────────────────────────────────────────────────────
  const totalDirectEmissions =
    Number(fC?.directEmissions)            ||
    Number(fC?.totalDirectEmissions)       ||
    Number(fC?.results?.ghgResult?.direct) || 0;
  const totalIndirectEmissions =
    Number(fC?.indirectEmissions)              ||
    Number(fC?.totalIndirectEmissions)         ||
    Number(fC?.results?.ghgResult?.indirect)   || 0;

  // ── Enrich FormD processes with FormA fallbacks ────────────────────────────
  const dProcesses = fD?.processes || [];
  const enrichedProcesses = dProcesses.map((proc, idx) => {
    const aProc = formAProcesses[idx] || {};
    return {
      ...proc,
      id:   proc.id   || proc.processId || aProc.id   || `P${idx + 1}`,
      name: proc.name || proc.title     || aProc.name || `P${idx + 1}`,
      aggregatedGoodsCategory: proc.aggregatedGoodsCategory || aProc.aggregatedGoodsCategory || "—",
    };
  });

  // ── FIX 2+3+4: processData — pass formEPrecursors to calculateSEE ─────────
  // Now returns full SEE (process + precursor + consumed process contribution layers)
  const processData = useMemo(() =>
    enrichedProcesses.map((proc) => {
      // Layer 1: process own SEE + FormE purchased precursor contributions
      const see       = DFormulas.calculateSEE(proc, formEPrecursors);
      const totalProd = see.productionLevelsTotalAuto || 0;
      const dirEm_net = see.dirEm_net || 0;
      const indirEm   = see.indir_em  || 0;

      const dirEm   = Number(proc.directlyAttributableEmissions) || 0;
      const heatNet =
        (Number(proc.measurableHeat?.amountImported)  || 0) * (Number(proc.measurableHeat?.emissionFactorImported)  || 0)
      - (Number(proc.measurableHeat?.amountExported)  || 0) * (Number(proc.measurableHeat?.emissionFactorExported)  || 0);
      const wasteGasNet = dirEm_net - dirEm - heatNet;

      const elecMWh  = Number(proc.electricityConsumption?.consumption || 0);
      const elecPerT = totalProd > 0 ? elecMWh / totalProd : 0;

      // Layer 2: FormE purchased precursor contributions to embedded electricity
      const precElecSum = formEPrecursors.reduce((sum, prec) => {
        const consumed = (prec.consumedInProcesses || []).find(cp =>
          cp.processId === (proc.processId || proc.id)
        );
        if (!consumed) return sum;
        const mass     = Number(consumed.amounts || 0);
        const massPerT = totalProd > 0 ? mass / totalProd : 0;
        return sum + massPerT * Number(prec.emissions?.secIndirect || 0);
      }, 0);

      // Layer 3: Consumed OTHER PROCESS contributions (e.g. P1 consumed in P2)
      // Guard: skip if amounts = 0, skip if circular (process consuming itself),
      // skip if the other process also consumes THIS process (avoid double-counting)
      const consumedProcSEESum = (proc.consumedOtherProcesses || []).reduce((sum, cp2) => {
        if (!cp2.processId) return sum;
        // Skip circular references
        if (cp2.processId === (proc.processId || proc.id)) return sum;
        const otherProc = enrichedProcesses.find(p => (p.processId || p.id) === cp2.processId);
        if (!otherProc) return sum;
        const thisProcIdx  = enrichedProcesses.findIndex(p => (p.processId || p.id) === (proc.processId || proc.id));
        const otherProcIdx = enrichedProcesses.findIndex(p => (p.processId || p.id) === cp2.processId);
        if (thisProcIdx <= otherProcIdx) return sum; // skip — only downstream processes add contributions
        const otherSee  = DFormulas.calculateSEE(otherProc, formEPrecursors);
        const otherProd = otherSee.productionLevelsTotalAuto || 0;
        // Auto-derive consumed amount if not explicitly entered:
        // Excel derives it as: upstream_total_production − upstream_sold_to_market
        const explicitQty = Number(cp2.amounts);
        const derivedQty  = explicitQty > 0
          ? explicitQty
          : otherProd - Number(otherProc.productionDetails?.amountsProducedForMarket || 0);
        const qty      = derivedQty > 0 ? derivedQty : 0;
        if (!qty) return sum;
        const massPerT   = totalProd > 0 ? qty / totalProd : 0;
        // Use FULL SEE (not process-only) — downstream process inherits full embedded
        // emissions of upstream including upstream's own precursor contributions
        const otherSEd   = otherSee.see_direct  ?? 0;
        const otherSEi   = otherSee.see_indirect ?? 0;
        const otherElec  = Number(otherProc.electricityConsumption?.consumption || 0);
        const otherElecT = otherProd > 0 ? otherElec / otherProd : 0;
        return {
          direct:   sum.direct   + otherSEd  * massPerT,
          indirect: sum.indirect + otherSEi  * massPerT,
          elec:     sum.elec     + otherElecT * massPerT,
        };
      }, { direct: 0, indirect: 0, elec: 0 });

      const embElecPerT = elecPerT + precElecSum + consumedProcSEESum.elec;

      const cp = (fF?.carbonPriceRows || []).find(r => r.processId === proc.id) || {};

      return {
        id:           proc.id,
        name:         proc.name,
        goodsCat:     proc.aggregatedGoodsCategory,
        dirEm,
        heatEm:       heatNet,
        wgEm:         wasteGasNet,
        totalDirect:  dirEm_net,
        indirect:     indirEm,
        activityLevel: totalProd,
        unit:         "t",
        // SE = process-only (no precursors, no consumed processes)
        se_direct:    see.see_direct_process  ?? see.see_direct,
        se_indirect:  see.see_indirect_process ?? see.see_indirect,
        // SEE = full: process + purchased precursors + consumed other processes
        see_direct:   see.see_direct  + consumedProcSEESum.direct,
        see_indirect: see.see_indirect + consumedProcSEESum.indirect,
        elecPerT,
        embElecPerT,
        cpDue:        Number(cp.cp_due_per_produced_t_or_MWh || 0),
        rebate:       Number(cp.rebate_per_produced_t_or_MWh || 0),
      };
    }),
    [enrichedProcesses, fF, formEPrecursors]
  );

  // ── GHG totals ─────────────────────────────────────────────────────────────
  const ghgTotals = useMemo(() => ({
    dirEm:       processData.reduce((s, p) => s + p.dirEm, 0),
    heatEm:      processData.reduce((s, p) => s + p.heatEm, 0),
    wgEm:        processData.reduce((s, p) => s + p.wgEm, 0),
    totalDirect: processData.reduce((s, p) => s + p.totalDirect, 0),
    indirect:    processData.reduce((s, p) => s + p.indirect, 0),
  }), [processData]);

  const restDirect     = totalDirectEmissions - ghgTotals.totalDirect;
  const restIndirect   = totalIndirectEmissions - ghgTotals.indirect;
  const restDirectPct  = totalDirectEmissions   ? restDirect   / totalDirectEmissions   : 0;
  const restIndirectPct= totalIndirectEmissions ? restIndirect / totalIndirectEmissions : 0;

  // ── Purchased precursors SEE (from FormE) ─────────────────────────────────
  const precursorData = useMemo(() => {
    return formAPrecursors.map((pp, idx) => {
      const epp = formEPrecursors.find(e =>
        e.formAPrecursorId === pp.id || e.id === pp.id
      ) || formEPrecursors[idx] || {};
      return {
        id:          pp.id || `PP${idx + 1}`,
        name:        pp.name || "—",
        goodsType:   pp.name || "—",
        seeDirect:   Number(epp.emissions?.seeDirect   || 0),
        seeIndirect: Number(epp.emissions?.seeIndirect || 0),
        elecPerT:    Number(epp.emissions?.secIndirect || 0),
      };
    });
  }, [formAPrecursors, formEPrecursors]);

  // ── FIX 5+6: Detailed overview — read from FormE consumedInProcesses ───────
  const detailedProcesses = useMemo(() =>
    enrichedProcesses.map((proc, pIdx) => {
      // FULL SEE for this process
      const see       = DFormulas.calculateSEE(proc, formEPrecursors);
      const totalProd = see.productionLevelsTotalAuto || 0;
      const elecMWh   = Number(proc.electricityConsumption?.consumption || 0);
      const elecPerT  = totalProd > 0 ? elecMWh / totalProd : 0;
      const procId    = proc.processId || proc.id;

      const cp = (fF?.carbonPriceRows || []).find(r => r.processId === procId) || {};

      // Sub-rows come from TWO sources — matching Excel exactly:
      // 1. Other PROCESSES consumed in this process (FormD consumedOtherProcesses) — e.g. P1 in P2
      // 2. Purchased PRECURSORS consumed in this process (FormE consumedInProcesses) — e.g. PP1-PP4
      
      // ── Source 1: Consumed processes from FormD ───────────────────────────
      // Only show processes that are UPSTREAM of this one (lower index = earlier process)
      // This prevents P1 showing P2 as a sub-row when P2 consumes P1, not the other way
      const thisProcIdx = enrichedProcesses.findIndex(p =>
        (p.processId || p.id) === (proc.processId || proc.id)
      );
      const consumedProcessRows = (proc.consumedOtherProcesses || [])
        .filter(cp2 => {
          if (!cp2.processId) return false;
          if (cp2.processId === (proc.processId || proc.id)) return false;
          const otherIdx = enrichedProcesses.findIndex(p => (p.processId || p.id) === cp2.processId);
          if (otherIdx === -1) return false;
          if (thisProcIdx <= otherIdx) return false; // only upstream processes (lower index)
          // Include if explicit amounts entered OR if we can auto-derive from production data
          const otherProc = enrichedProcesses[otherIdx];
          const otherSee  = DFormulas.calculateSEE(otherProc, formEPrecursors);
          const otherProd = otherSee.productionLevelsTotalAuto || 0;
          const derivable = otherProd - Number(otherProc.productionDetails?.amountsProducedForMarket || 0);
          return Number(cp2.amounts) > 0 || derivable > 0;
        })
        .map((cp2, ci) => {
          const otherProc = enrichedProcesses.find(p => (p.processId || p.id) === cp2.processId);
          const otherSee  = otherProc
            ? DFormulas.calculateSEE(otherProc, formEPrecursors)
            : { see_direct: 0, see_indirect: 0, see_total: 0, productionLevelsTotalAuto: 0 };
          const otherProd = otherSee.productionLevelsTotalAuto || 0;
          // Auto-derive qty: explicit if entered, else upstream_production − upstream_sold_to_market
          const explicitQty = Number(cp2.amounts);
          const derivedQty  = explicitQty > 0
            ? explicitQty
            : otherProd - Number(otherProc?.productionDetails?.amountsProducedForMarket || 0);
          const qty       = Math.max(derivedQty, 0);
          const massShare = totalProd > 0 ? qty / totalProd : 0;

          const seeDirect   = otherSee.see_direct   || 0;
          const seeIndirect = otherSee.see_indirect || 0;
          const seeTotal    = otherSee.see_total    || 0;
          const elecPerT    = otherProc
            ? (Number(otherProc.electricityConsumption?.consumption || 0) /
               Math.max(otherSee.productionLevelsTotalAuto || 1, 1)) * massShare
            : 0;

          return {
            rowNum:      ci + 1,
            id:          cp2.processId,
            name:        otherProc?.name || otherProc?.title || cp2.processId || "—",
            goodsType:   otherProc?.aggregatedGoodsCategory || "—",
            massShare,
            isDefault:   false,
            isOwnRow:    false,
            isProcess:   true,   // flag — renders as "n.a." for default value
            seeDirect:   seeDirect   * massShare,
            seeIndirect: seeIndirect * massShare,
            seeTotal:    seeTotal    * massShare,
            embDirect:   seeDirect   * qty,
            embIndirect: seeIndirect * qty,
            embTotal:    seeTotal    * qty,
            elecSrc:     otherProc?.electricityConsumption?.source || "—",
            elecPerT,
            countryCode: "—",
          };
        });

      // ── Source 2: Consumed precursors from FormE ──────────────────────────
      const procName = proc.title || proc.name || "";
      const consumedPrecs = formEPrecursors
        .map((prec, pi) => {
          // Find how much of this precursor is consumed in THIS process
          // Flexible match — handles processId, process name, or linkedProcessId mismatch
          const consumed = (prec.consumedInProcesses || []).find(cp2 => {
            if (!cp2.processId && !cp2.process) return false;
            return (
              cp2.processId === procId ||
              cp2.processId === proc.id ||
              cp2.processId === proc.processId ||
              cp2.process === procName ||
              cp2.processId === procName
            );
          });
          if (!consumed || !Number(consumed.amounts)) return null;

          const qty        = Number(consumed.amounts);
          const massShare  = totalProd > 0 ? qty / totalProd : 0;
          const seeDirect  = Number(prec.emissions?.seeDirect   || 0);
          const seeIndirect= Number(prec.emissions?.seeIndirect || 0);
          const seeTotal   = seeDirect + seeIndirect;
          const elecPrecT  = Number(prec.emissions?.secIndirect || 0);
          const elecSrc    = prec.emissions?.eefIndirectSource   || "—";

          // Find FormA precursor for name + country
          const faPP = formAPrecursors.find(fp =>
            fp.id === prec.formAPrecursorId || fp.id === prec.id
          ) || {};

          return {
            rowNum:      pi + 1,
            id:          faPP.id || `PP${pi + 1}`,
            name:        faPP.name || prec.title || "—",
            goodsType:   faPP.goodsCategory || faPP.name || "—",
            massShare,
            isDefault:   prec.emissions?.sourceSeeDirect === "Default",
            // Per-tonne values weighted by mass share
            seeDirect:   seeDirect   * massShare,
            seeIndirect: seeIndirect * massShare,
            seeTotal:    seeTotal    * massShare,
            // Absolute embedded emissions
            embDirect:   seeDirect   * qty,
            embIndirect: seeIndirect * qty,
            embTotal:    seeTotal    * qty,
            elecSrc,
            elecPerT:    elecPrecT   * massShare,
            countryCode: faPP.countryCode || "—",
          };
        })
        .filter(Boolean); // remove nulls (precursors not consumed in this process)

      // ── Source 2b: Inherited precursors via consumed process chain ────────
      // If P2 consumes P1, and P1 has PP1-PP4 as sub-rows, those precursors
      // also appear in P2's detailed overview (Excel rows 203-206 for P2).
      // Mass share = (precursor consumed in upstream / upstream production) × (upstream consumed in this / this production)
      const inheritedPrecs = [];
      consumedProcessRows.forEach((procRow) => {
        const upstreamProc = enrichedProcesses.find(p =>
          (p.processId || p.id) === procRow.id
        );
        if (!upstreamProc) return;
        const upstreamSee  = DFormulas.calculateSEE(upstreamProc, formEPrecursors);
        const upstreamProd = upstreamSee.productionLevelsTotalAuto || 1;
        const upstreamProcId = upstreamProc.processId || upstreamProc.id;

        formEPrecursors.forEach((prec, pi) => {
          // Skip if this precursor is already directly in consumedPrecs for this process
          const alreadyDirect = consumedPrecs.some(cp2 => {
            const faPP2 = formAPrecursors.find(fp =>
              fp.id === prec.formAPrecursorId || fp.id === prec.id
            ) || {};
            return cp2.id === (faPP2.id || `PP${pi + 1}`);
          });
          if (alreadyDirect) return;

          // Check if this precursor is consumed in the upstream process
          // Use flexible matching — handles processId, process name, or linkedProcessId mismatch
          const upstreamName = upstreamProc.title || upstreamProc.name || "";
          const consumedInUpstream = (prec.consumedInProcesses || []).find(cp2 => {
            if (!cp2.processId && !cp2.process) return false;
            return (
              cp2.processId === upstreamProcId ||
              cp2.processId === upstreamProc.id ||
              cp2.processId === upstreamProc.processId ||
              cp2.process === upstreamName ||
              cp2.processId === upstreamName
            );
          });
          if (!consumedInUpstream || !Number(consumedInUpstream.amounts)) return;

          const upstreamQty   = Number(consumedInUpstream.amounts);
          // massShare in terms of THIS process = (prec qty in upstream / upstream prod) × (upstream qty / this prod)
          const massShare     = totalProd > 0
            ? (upstreamQty / upstreamProd) * procRow.massShare
            : 0;
          const qty           = massShare * totalProd;

          const seeDirect     = Number(prec.emissions?.seeDirect   || 0);
          const seeIndirect   = Number(prec.emissions?.seeIndirect || 0);
          const seeTotal      = seeDirect + seeIndirect;
          const elecPrecT     = Number(prec.emissions?.secIndirect || 0);
          const elecSrc       = prec.emissions?.eefIndirectSource  || "—";

          const faPP = formAPrecursors.find(fp =>
            fp.id === prec.formAPrecursorId || fp.id === prec.id
          ) || {};

          inheritedPrecs.push({
            rowNum:      pi + 1,
            id:          faPP.id || `PP${pi + 1}`,
            name:        faPP.name || prec.title || "—",
            goodsType:   faPP.goodsCategory || faPP.name || "—",
            massShare,
            isDefault:   prec.emissions?.sourceSeeDirect === "Default",
            seeDirect:   seeDirect   * massShare,
            seeIndirect: seeIndirect * massShare,
            seeTotal:    seeTotal    * massShare,
            embDirect:   seeDirect   * qty,
            embIndirect: seeIndirect * qty,
            embTotal:    seeTotal    * qty,
            elecSrc,
            elecPerT:    elecPrecT   * massShare,
            countryCode: faPP.countryCode || "—",
          });
        });
      });

      // Combine direct + inherited precursor sub-rows (no duplicates)
      const allConsumedPrecs = [...consumedPrecs, ...inheritedPrecs];
      const seeDirect_proc   = see.see_direct_process  ?? see.see_direct;
      const seeIndirect_proc = see.see_indirect_process ?? see.see_indirect;
      const seeTotal_proc    = seeDirect_proc + seeIndirect_proc;

      // FULL SEE for Total row = own process + consumed processes (full SEE × mass share)
      // The consumed process row already includes its own precursor contributions baked in,
      // so we do NOT add inherited precursors here — that would double-count them.
      // Inherited precursors are still shown as separate sub-rows for transparency,
      // but their contributions are already present in the consumed process's full SEE.
      const consumedProcSeeDir = consumedProcessRows.reduce((s, r) => s + (r.seeDirect   || 0), 0);
      const consumedProcSeeInd = consumedProcessRows.reduce((s, r) => s + (r.seeIndirect || 0), 0);

      const fullSeeDirect   = (see.see_direct   || 0) + consumedProcSeeDir;
      const fullSeeIndirect = (see.see_indirect || 0) + consumedProcSeeInd;
      const fullSeeTotal    = fullSeeDirect + fullSeeIndirect;

      // EmbEm = SEE × production
      const embDirect_full  = fullSeeDirect   * totalProd;
      const embIndirect_full= fullSeeIndirect * totalProd;
      const embTotal_full   = fullSeeTotal    * totalProd;

      // Embedded electricity = process + precursor elec + consumed process elec
      const precElecSum     = allConsumedPrecs.reduce((s, cp2) => s + cp2.elecPerT, 0);
      const procElecSum     = consumedProcessRows.reduce((s, cp2) => s + cp2.elecPerT, 0);
      const embElecPerT     = elecPerT + precElecSum + procElecSum;

      // Default value share = fraction of EmbEm from rows marked as default
      const defaultEmbEm = allConsumedPrecs
        .filter(cp2 => cp2.isDefault)
        .reduce((s, cp2) => s + cp2.embDirect, 0);
      const totalEmbEm   = embDirect_full || 1;
      const defaultShare = totalEmbEm > 0 ? defaultEmbEm / totalEmbEm : 0;

      // Own process row entry (mass share = 1.0, row shows process itself)
      const ownRow = {
        rowNum:      null,
        id:          procId,
        name:        proc.name || "—",
        goodsType:   proc.aggregatedGoodsCategory || "—",
        massShare:   1.0,
        isDefault:   false,
        seeDirect:   seeDirect_proc,
        seeIndirect: seeIndirect_proc,
        seeTotal:    seeTotal_proc,
        embDirect:   seeDirect_proc   * totalProd,
        embIndirect: seeIndirect_proc * totalProd,
        embTotal:    seeTotal_proc    * totalProd,
        elecSrc:     proc.electricityConsumption?.source || "—",
        elecPerT,
        countryCode: "—",
        isOwnRow:    true,
      };

      return {
        id:       procId,
        name:     proc.name || "—",
        goodsCat: proc.aggregatedGoodsCategory || "—",
        totalProd,
        // FULL SEE totals for header row
        seeDirect:    fullSeeDirect,
        seeIndirect:  fullSeeIndirect,
        seeTotal:     fullSeeTotal,
        embDirect:    embDirect_full,
        embIndirect:  embIndirect_full,
        embTotal:     embTotal_full,
        elecPerT,
        embElecPerT,
        defaultShare,
        elecSrc:  proc.electricityConsumption?.source || "—",
        cpDue:    Number(cp.cp_due_per_produced_t_or_MWh || 0),
        rebate:   Number(cp.rebate_per_produced_t_or_MWh || 0),
        // Sub-rows: own process row + each consumed precursor
        subRows:  [ownRow, ...consumedProcessRows, ...allConsumedPrecs],
      };
    }),
    [enrichedProcesses, fF, formEPrecursors, formAPrecursors]
  );

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{S}</style>
      <div className="sp">

        {/* Nav bar */}
        <div className="sp-nav">
          <span className="sp-nav-title">Summary of production processes</span>
          <span className="sp-nav-link">Table of contents</span>
          <span className="sp-nav-link">Further Guidance</span>
          <span className="sp-nav-link">Summary_Products</span>
          <span
            className="sp-nav-link"
            onClick={() => exportSummaryExcel(allFormData)}
            style={{ backgroundColor: "#C6EFCE", color: "#1a5e30", fontWeight: 700, borderColor: "#82c891" }}
          >
            ⬇ Export to Excel
          </span>
        </div>

        {/* ══ SECTION 1 ══ */}
        <div className="sp-section">
          <div className="sp-band">1. Summary of the installation, processes and production routes</div>
          <div className="sp-body">
            <div className="sp-sub">1. Summary of the installation</div>
            <table className="sp-inst-table">
              <tbody>
                <tr>
                  <td className="c-label">Name of the installation (English name):</td>
                  <td className="c-green">{inst.name}</td>
                  <td style={{ width: 20 }} />
                  <td className="c-label" style={{ textAlign: "right" }}>Reporting period start:</td>
                  <td className="c-green c-center">{inst.periodStart}</td>
                </tr>
                <tr>
                  <td className="c-label">Street, Number:</td>
                  <td className="c-green">{inst.street}</td>
                  <td />
                  <td className="c-label" style={{ textAlign: "right" }}>Reporting period end:</td>
                  <td className="c-green c-center">{inst.periodEnd}</td>
                </tr>
                <tr>
                  <td className="c-label">Economic activity:</td>
                  <td className="c-green">{inst.economic}</td>
                  <td colSpan={3} />
                </tr>
                <tr>
                  <td className="c-label">City:</td>
                  <td className="c-green">{inst.city}</td>
                  <td />
                  <td className="c-label" style={{ textAlign: "right" }}>Carbon price instrument:</td>
                  <td className="c-yellow">{inst.carbonPrice}</td>
                </tr>
                <tr>
                  <td className="c-label">Country:</td>
                  <td className="c-green">{inst.country}</td>
                  <td colSpan={3} className="c-label" style={{ fontSize: 9, color: "#666", whiteSpace: "normal" }}>
                    Description and indication of legal act for the carbon price, and for possible rebate or other form of compensation obtained.
                  </td>
                </tr>
                <tr>
                  <td className="c-label">UNLOCODE:</td>
                  <td className="c-green">{inst.unlocode}</td>
                  <td colSpan={3} />
                </tr>
                <tr>
                  <td className="c-label">Coordinates of the main emission source (latitude):</td>
                  <td className="c-green">{inst.lat}</td>
                  <td />
                  <td className="c-label" style={{ textAlign: "right" }}>Any additional information:</td>
                  <td className="c-yellow">{inst.addlInfo}</td>
                </tr>
                <tr>
                  <td className="c-label">Coordinates of the main emission source (longitude):</td>
                  <td className="c-green">{inst.lon}</td>
                  <td colSpan={3} className="c-label" style={{ fontSize: 9, color: "#666", whiteSpace: "normal" }}>
                    Any additional information you would like to provide to the reporting declarant.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="sp-body">
            <div className="sp-sub">2. Summary of the production processes, included precursors and production routes, where relevant</div>

            {/* (a) Aggregated goods */}
            <table style={{ marginBottom: 8 }}>
              <thead>
                <tr>
                  <th className="c-hdr" style={{ minWidth: 40 }}>(a)</th>
                  <th className="c-hdr" style={{ minWidth: 180 }}>Aggregated good produced</th>
                  <th className="c-hdr" style={{ minWidth: 120 }}>Routes</th>
                  {["Route 1","Route 2","Route 3","Route 4","Route 5","Route 6"].map(r => (
                    <th key={r} className="c-shdr" style={{ minWidth: 110 }}>{r}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {aggregatedGoods.filter(g => g.category).map((g, i) => (
                  <tr key={g._uuid || i}>
                    <td className="c-id">{g.id}</td>
                    <td className="c-green">{g.category}</td>
                    <td className="c-green">{g.primaryRoute || "All production routes"}</td>
                    {["route1","route2","route3","route4","route5","route6"].map(f => (
                      <td key={f} className="c-green">{g[f] || "—"}</td>
                    ))}
                  </tr>
                ))}
                {aggregatedGoods.filter(g => g.category).length === 0 && (
                  <tr><td colSpan={9} className="c-grey c-center">No aggregated goods in Form A</td></tr>
                )}
              </tbody>
            </table>

            {/* (b) Production processes */}
            <table style={{ marginBottom: 8 }}>
              <thead>
                <tr>
                  <th className="c-hdr" style={{ minWidth: 40 }}>(b)</th>
                  <th className="c-hdr" style={{ minWidth: 160 }}>Production process</th>
                  <th className="c-hdr" style={{ minWidth: 180 }}>Aggregated goods category</th>
                  {["1","2","3","4","5","6"].map(n => (
                    <th key={n} className="c-shdr c-center" style={{ minWidth: 80 }}>{n}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {formAProcesses.filter(p => p.name || p.aggregatedGoodsCategory).map((p, i) => (
                  <tr key={p._uuid || i}>
                    <td className="c-id">{p.id}</td>
                    <td className="c-green">{p.name || "—"}</td>
                    <td className="c-green">{p.aggregatedGoodsCategory || "—"}</td>
                    {["inc1","inc2","inc3","inc4","inc5","inc6"].map(f => (
                      <td key={f} className="c-green c-center">{p[f] || "—"}</td>
                    ))}
                  </tr>
                ))}
                {formAProcesses.filter(p => p.name || p.aggregatedGoodsCategory).length === 0 && (
                  <tr><td colSpan={9} className="c-grey c-center">No processes in Form A</td></tr>
                )}
              </tbody>
            </table>

            {/* (c) Purchased precursors — FIX 1: goodsCategory not linkedProcessId */}
            <table>
              <thead>
                <tr>
                  <th className="c-hdr" style={{ minWidth: 40 }}>(c)</th>
                  <th className="c-hdr" style={{ minWidth: 160 }}>Purchased precursor</th>
                  <th className="c-hdr" style={{ minWidth: 180 }}>Aggregated goods category</th>
                  {["Route 1","Route 2","Route 3","Route 4","Route 5"].map(r => (
                    <th key={r} className="c-shdr" style={{ minWidth: 110 }}>{r}</th>
                  ))}
                  <th className="c-shdr" style={{ minWidth: 80 }}>Country code</th>
                </tr>
              </thead>
              <tbody>
                {formAPrecursors.filter(p => p.name).map((pp, i) => (
                  <tr key={pp._uuid || i}>
                    <td className="c-id">{pp.id}</td>
                    <td className="c-green">{pp.name}</td>
                    {/* FIX 1: use goodsCategory (new field), fallback to linkedProcessId for old data */}
                    <td className="c-green">{pp.goodsCategory || pp.linkedProcessId || "—"}</td>
                    {["route1","route2","route3","route4","route5"].map(f => (
                      <td key={f} className="c-green">{pp[f] || "—"}</td>
                    ))}
                    <td className="c-green c-center">{pp.countryCode || "—"}</td>
                  </tr>
                ))}
                {formAPrecursors.filter(p => p.name).length === 0 && (
                  <tr><td colSpan={9} className="c-grey c-center">No purchased precursors in Form A</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ══ SECTION 2 ══ */}
        <div className="sp-section">
          <div className="sp-band">2. Greenhouse gas emissions balance and specific embedded emissions (SEE)</div>

          <div className="sp-body">
            <div className="sp-sub">1. GHG emissions balance of the installation and all production processes</div>

            {/* (a) GHG per process */}
            <table style={{ marginBottom: 8 }}>
              <thead>
                <tr>
                  <th className="c-hdr" style={{ minWidth: 40 }}>(a)</th>
                  <th className="c-hdr" style={{ minWidth: 160 }}>Production process</th>
                  <th className="c-hdr" style={{ minWidth: 180 }}>Aggregated goods category</th>
                  <th className="c-hdr" style={{ minWidth: 60 }}>Unit</th>
                  <th className="c-hdr" style={{ minWidth: 110 }}>DirEm*</th>
                  <th className="c-hdr" style={{ minWidth: 110 }}>Heat emissions</th>
                  <th className="c-hdr" style={{ minWidth: 120 }}>Waste gas emissions</th>
                  <th className="c-hdr" style={{ minWidth: 130 }}>Total direct emissions</th>
                  <th className="c-hdr" style={{ minWidth: 120 }}>Indirect, if relevant</th>
                  <th className="c-hdr" style={{ minWidth: 20 }} />
                  <th className="c-hdr" style={{ minWidth: 110 }}>Activity level</th>
                  <th className="c-hdr" style={{ minWidth: 50 }}>Unit</th>
                </tr>
              </thead>
              <tbody>
                {processData.length === 0 ? (
                  <tr><td colSpan={12} className="c-grey c-center">No process data from Form D</td></tr>
                ) : processData.map((p, i) => (
                  <tr key={i}>
                    <td className="c-id">{p.id}</td>
                    <td className="c-green">{p.name}</td>
                    <td className="c-green">{p.goodsCat}</td>
                    <td className="c-white c-center">tCO2e</td>
                    <td className="c-green c-num">{fmt(p.dirEm)}</td>
                    <td className="c-green c-num">{p.heatEm === 0 ? "—" : fmt(p.heatEm)}</td>
                    <td className="c-green c-num">{p.wgEm   === 0 ? "—" : fmt(p.wgEm)}</td>
                    <td className="c-green c-num">{fmt(p.totalDirect)}</td>
                    <td className="c-green c-num">{fmt(p.indirect)}</td>
                    <td className="c-white" />
                    <td className="c-green c-num">{fmt(p.activityLevel, 0)}</td>
                    <td className="c-white c-center">{p.unit}</td>
                  </tr>
                ))}
                <tr>
                  <td className="c-total" colSpan={2} style={{ textAlign: "right" }}>Total</td>
                  <td className="c-total" /><td className="c-total c-center">tCO2e</td>
                  <td className="c-total c-num">{fmt(ghgTotals.dirEm)}</td>
                  <td className="c-total c-num">{ghgTotals.heatEm === 0 ? "—" : fmt(ghgTotals.heatEm)}</td>
                  <td className="c-total c-num">{ghgTotals.wgEm   === 0 ? "—" : fmt(ghgTotals.wgEm)}</td>
                  <td className="c-total c-num">{fmt(ghgTotals.totalDirect)}</td>
                  <td className="c-total c-num">{fmt(ghgTotals.indirect)}</td>
                  <td className="c-total" colSpan={3} />
                </tr>
              </tbody>
            </table>

            {/* (b) Comparison */}
            <table style={{ marginBottom: 8 }}>
              <thead>
                <tr>
                  <th className="c-hdr" style={{ minWidth: 40 }}>(b)</th>
                  <th className="c-hdr" style={{ minWidth: 400 }}>Comparison:</th>
                  <th className="c-hdr" style={{ minWidth: 60 }}>Unit</th>
                  <th className="c-hdr" style={{ minWidth: 160 }}>Total direct emissions</th>
                  <th className="c-hdr" style={{ minWidth: 160 }}>Total indirect emissions</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["i.",   "Total emissions of the installation (from sheet \"C_Emissions&Energy\")", totalDirectEmissions, totalIndirectEmissions],
                  ["ii.",  "Rest (= emissions not attributed to CBAM processes)",                    restDirect,           restIndirect],
                ].map(([n, label, d, i]) => (
                  <tr key={n}>
                    <td className="c-white c-center">{n}</td>
                    <td className="c-label">{label}</td>
                    <td className="c-white c-center">tCO2e</td>
                    <td className="c-green c-num">{fmt(d)}</td>
                    <td className="c-green c-num">{fmt(i)}</td>
                  </tr>
                ))}
                <tr>
                  <td className="c-white c-center">iii.</td>
                  <td className="c-label">Rest (expressed as % of total)</td>
                  <td className="c-white c-center">—</td>
                  <td className="c-green c-num">{fmtPct(restDirectPct)}</td>
                  <td className="c-green c-num">{fmtPct(restIndirectPct)}</td>
                </tr>
                <tr>
                  <td colSpan={5} className="c-note" style={{ whiteSpace: "normal", padding: "3px 5px" }}>
                    Note: the system boundaries between production processes, indirect emissions and the installation's total emissions might not be consistent.
                    Therefore, values for "Rest" different from zero are not necessarily indicative of any omissions or errors being made in this report.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* 2.2 SEE */}
          <div className="sp-body">
            <div className="sp-sub">2. Specific direct and indirect (embedded) emissions</div>

            {/* Abbreviation key */}
            <table className="sp-abbr-table" style={{ marginBottom: 10, width: "auto" }}>
              <tbody>
                {[
                  ["SE (direct)",    "Specific direct emissions of the production process, i.e. excluding any embedded emissions from any precursors consumed in the process."],
                  ["SEE (direct)",   "Specific embedded direct emissions of the production process, i.e. including any embedded emissions from any precursors consumed in the process."],
                  ["SE (indirect)",  "Specific indirect emissions of the production process, i.e. excluding any embedded indirect emissions from any precursors consumed in the process."],
                  ["SEE (indirect)", "Specific embedded indirect emissions of the production process, i.e. including any embedded indirect emissions from any precursors consumed in the process."],
                  ["Electricity consumed",  "Specific electricity consumption within the production process."],
                  ["Embedded electricity",  "Specific embedded electricity consumption of the production process, including any embedded electricity consumption in other production processes within the installation and purchased precursors."],
                  ["CP due (per produced t or MWh)", "This information is taken from the \"tool to calculate the carbon price due\" in sheet \"F_Tools\", where relevant."],
                  ["Rebate (per produced t or MWh)", "This information is taken from the \"tool to calculate the carbon price due\" in sheet \"F_Tools\", where relevant."],
                ].map(([k, v]) => (
                  <tr key={k}>
                    <td className="c-label" style={{ padding: "1px 8px 1px 4px", fontWeight: 700, color: "#254244" }}>{k}</td>
                    <td style={{ padding: "1px 4px", maxWidth: 600, whiteSpace: "normal", color: "#444", fontSize: 10 }}>{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* (a) Production processes SEE — FIX 2+3 */}
            <table style={{ marginBottom: 8 }}>
              <thead>
                <tr>
                  <th className="c-hdr" rowSpan={2} style={{ minWidth: 40 }}>(a)</th>
                  <th className="c-hdr" rowSpan={2} style={{ minWidth: 160 }}>Production process</th>
                  <th className="c-hdr" rowSpan={2} style={{ minWidth: 180 }}>Aggregated goods category</th>
                  <th className="c-hdr" style={{ minWidth: 100 }}>SE (direct)</th>
                  <th className="c-hdr" style={{ minWidth: 100 }}>SEE (direct)</th>
                  <th className="c-hdr" style={{ minWidth: 100 }}>SE (indirect)</th>
                  <th className="c-hdr" style={{ minWidth: 100 }}>SEE (indirect)</th>
                  <th className="c-hdr" style={{ minWidth: 110 }}>Electricity consumed</th>
                  <th className="c-hdr" style={{ minWidth: 110 }}>Embedded electricity</th>
                  <th className="c-hdr" style={{ minWidth: 130 }}>CP due (per t or MWh)</th>
                  <th className="c-hdr" style={{ minWidth: 120 }}>Rebate (per t or MWh)</th>
                </tr>
                <tr>
                  {["tCO2e/t","tCO2e/t","tCO2e/t","tCO2e/t","MWh/t","MWh/t","",""].map((u, i) => (
                    <th key={i} className="c-shdr c-center">{u}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {processData.length === 0 ? (
                  <tr><td colSpan={11} className="c-grey c-center">No process data</td></tr>
                ) : processData.map((p, i) => (
                  <tr key={i}>
                    <td className="c-id">{p.id}</td>
                    <td className="c-green">{p.name}</td>
                    <td className="c-green">{p.goodsCat}</td>
                    {/* FIX 3: SE(direct) = process-only, SEE(direct) = full incl. precursors */}
                    <td className="c-green c-num">{fmt(p.se_direct,   3)}</td>
                    <td className="c-green c-num">{fmt(p.see_direct,  3)}</td>
                    <td className="c-green c-num">{fmt(p.se_indirect, 3)}</td>
                    <td className="c-green c-num">{fmt(p.see_indirect,3)}</td>
                    {/* FIX 4: separate process elec vs embedded elec */}
                    <td className="c-green c-num">{fmt(p.elecPerT,    3)}</td>
                    <td className="c-green c-num">{fmt(p.embElecPerT, 3)}</td>
                    <td className="c-green c-num">{p.cpDue  ? fmt(p.cpDue,  3) : "—"}</td>
                    <td className="c-green c-num">{p.rebate ? fmt(p.rebate, 3) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* (b) Purchased precursors SEE */}
            <table>
              <thead>
                <tr>
                  <th className="c-hdr" rowSpan={2} style={{ minWidth: 40 }}>(b)</th>
                  <th className="c-hdr" rowSpan={2} style={{ minWidth: 160 }}>Purchased precursors</th>
                  <th className="c-hdr" rowSpan={2} style={{ minWidth: 180 }}>Type of precursor</th>
                  <th className="c-hdr" style={{ minWidth: 100 }} />
                  <th className="c-hdr" style={{ minWidth: 100 }}>SEE (direct)</th>
                  <th className="c-hdr" style={{ minWidth: 100 }} />
                  <th className="c-hdr" style={{ minWidth: 100 }}>SEE (indirect)</th>
                  <th className="c-hdr" style={{ minWidth: 110 }}>Electricity consumed</th>
                  <th className="c-hdr" colSpan={3} />
                </tr>
                <tr>
                  <th className="c-shdr" />
                  <th className="c-shdr c-center">tCO2e/t</th>
                  <th className="c-shdr" />
                  <th className="c-shdr c-center">tCO2e/t</th>
                  <th className="c-shdr c-center">MWh/t</th>
                  <th className="c-shdr" colSpan={3} />
                </tr>
              </thead>
              <tbody>
                {precursorData.length === 0 ? (
                  <tr><td colSpan={11} className="c-grey c-center">No purchased precursors defined</td></tr>
                ) : precursorData.map((pp, i) => (
                  <tr key={i}>
                    <td className="c-id">{pp.id}</td>
                    <td className="c-green">{pp.name}</td>
                    <td className="c-green">{pp.goodsType}</td>
                    <td className="c-white" />
                    <td className="c-green c-num">{fmt(pp.seeDirect,   3)}</td>
                    <td className="c-white" />
                    <td className="c-green c-num">{fmt(pp.seeIndirect, 3)}</td>
                    <td className="c-green c-num">{fmt(pp.elecPerT,    3)}</td>
                    <td className="c-white" colSpan={3} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ══ SECTION 3 ══ */}
        <div className="sp-section">
          <div className="sp-band">3. Detailed overview of each production process</div>
          <div className="sp-body">

            {/* Abbreviation legend */}
            <table className="sp-abbr-table" style={{ marginBottom: 10, width: "auto" }}>
              <tbody>
                {[
                  ["Mass share",              "This is the specific consumption of each precursor per unit of products produced by the relevant 'production process'."],
                  ["(Share of) Default value","Indicates whether the embedded direct emissions are determined based on default values (TRUE) or actual measurements (FALSE). For the whole process, this indicator will calculate the share of embedded emissions determined by default values."],
                  ["SEE (direct)",            "Specific embedded direct emissions of the production process, including any embedded emissions from any precursors consumed in the process."],
                  ["SEE (indirect)",          "Specific embedded indirect emissions of the production process, including any embedded indirect emissions from any precursors consumed in the process."],
                  ["SEE (total)",             "Total (direct + indirect) specific embedded emissions of the production process."],
                  ["EmbEm (direct)",          "Embedded direct emissions of the production process, including any embedded emissions from any precursors consumed in the process."],
                  ["EmbEm (indirect)",        "Embedded indirect emissions of the production process."],
                  ["EmbEm (total)",           "Total (direct + indirect) embedded emissions of the production process."],
                  ["Source of electricity EF","The source or method based on which the electricity emission factor (EF) was determined."],
                  ["Embedded electricity",    "Specific embedded electricity consumption the production process, including any electricity embedded in any precursors consumed in the process."],
                  ["Country code",            "This is the country in which the relevant precursor is produced, where imported from outside the own installation."],
                ].map(([k, v]) => (
                  <tr key={k}>
                    <td className="c-label" style={{ padding: "1px 8px 1px 4px", fontWeight: 700, color: "#254244" }}>{k}</td>
                    <td style={{ padding: "1px 4px", maxWidth: 600, whiteSpace: "normal", color: "#444", fontSize: 10 }}>{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* One block per process — FIX 5+6 */}
            {detailedProcesses.length === 0 && (
              <div className="c-grey c-center" style={{ padding: 12 }}>No process data from Form D</div>
            )}
            {detailedProcesses.map((proc, pi) => (
              <div key={pi} style={{ marginBottom: 16 }}>
                <div className="sp-sub" style={{ background: "#E5F0F1", padding: "3px 8px", fontWeight: 700, color: "#254244", marginBottom: 2 }}>
                  {pi + 1} &nbsp; {proc.name || "—"} &nbsp;|&nbsp; Aggregated goods category
                </div>
                <table>
                  <thead>
                    <tr>
                      <th className="c-hdr" style={{ minWidth: 60 }}>Row</th>
                      <th className="c-hdr" style={{ minWidth: 180 }}>Aggregated goods category</th>
                      <th className="c-hdr" style={{ minWidth: 80 }}>Mass share<br /><span style={{ fontWeight: 400, fontSize: 9 }}>t/t</span></th>
                      <th className="c-hdr" style={{ minWidth: 100 }}>(Share of) Default value</th>
                      <th className="c-hdr" style={{ minWidth: 90 }}>SEE (direct)<br /><span style={{ fontWeight: 400, fontSize: 9 }}>tCO2e/t</span></th>
                      <th className="c-hdr" style={{ minWidth: 90 }}>SEE (indirect)<br /><span style={{ fontWeight: 400, fontSize: 9 }}>tCO2e/t</span></th>
                      <th className="c-hdr" style={{ minWidth: 90 }}>SEE (total)<br /><span style={{ fontWeight: 400, fontSize: 9 }}>tCO2e/t</span></th>
                      <th className="c-hdr" style={{ minWidth: 100 }}>EmbEm (direct)<br /><span style={{ fontWeight: 400, fontSize: 9 }}>tCO2e</span></th>
                      <th className="c-hdr" style={{ minWidth: 100 }}>EmbEm (indirect)<br /><span style={{ fontWeight: 400, fontSize: 9 }}>tCO2e</span></th>
                      <th className="c-hdr" style={{ minWidth: 100 }}>EmbEm (total)<br /><span style={{ fontWeight: 400, fontSize: 9 }}>tCO2e</span></th>
                      <th className="c-hdr" style={{ minWidth: 100 }}>Source of electricity EF</th>
                      <th className="c-hdr" style={{ minWidth: 90 }}>Embedded electricity<br /><span style={{ fontWeight: 400, fontSize: 9 }}>MWh/t</span></th>
                      <th className="c-hdr" style={{ minWidth: 60 }}>Country code</th>
                      <th className="c-hdr" style={{ minWidth: 100 }}>CP due (per t or MWh)</th>
                      <th className="c-hdr" style={{ minWidth: 90 }}>Rebate (per t or MWh)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* TOTAL row — full SEE including all precursor contributions */}
                    <tr style={{ fontWeight: 700, background: "#E5F0F1" }}>
                      <td className="c-id">Total</td>
                      <td className="c-green">{proc.goodsCat}</td>
                      <td className="c-white c-center">—</td>
                      <td className="c-green c-center">{fmtPct(proc.defaultShare)}</td>
                      <td className="c-green c-num">{fmt(proc.seeDirect,   3)}</td>
                      <td className="c-green c-num">{fmt(proc.seeIndirect, 3)}</td>
                      <td className="c-green c-num">{fmt(proc.seeTotal,    3)}</td>
                      <td className="c-green c-num">{fmt(proc.embDirect)}</td>
                      <td className="c-green c-num">{fmt(proc.embIndirect)}</td>
                      <td className="c-green c-num">{fmt(proc.embTotal)}</td>
                      <td className="c-white c-center">—</td>
                      <td className="c-green c-num">{fmt(proc.embElecPerT, 3)}</td>
                      <td className="c-white c-center">—</td>
                      <td className="c-green c-num">{proc.cpDue  ? fmt(proc.cpDue,  3) : "—"}</td>
                      <td className="c-green c-num">{proc.rebate ? fmt(proc.rebate, 3) : "—"}</td>
                    </tr>

                    {/* Sub-rows: own process row + consumed processes + consumed precursors */}
                    {proc.subRows.map((row, ri) => (
                      <tr key={ri} style={row.isOwnRow ? { background: "#f5fff5" } : {}}>
                        <td className="c-id" style={{ fontWeight: row.isOwnRow ? 700 : 400, fontSize: 10 }}>
                          {row.isOwnRow ? row.id : row.rowNum}
                        </td>
                        <td className="c-green" style={{ paddingLeft: row.isOwnRow ? 5 : 12 }}>
                          {row.name}
                        </td>
                        <td className="c-green c-num">{row.isOwnRow ? "1.000" : fmt(row.massShare, 3)}</td>
                        <td className="c-green c-center">
                          {row.isOwnRow ? "—" : row.isProcess ? "n.a." : (row.isDefault ? "TRUE" : "FALSE")}
                        </td>
                        <td className="c-green c-num">{fmt(row.seeDirect,   3)}</td>
                        <td className="c-green c-num">{fmt(row.seeIndirect, 3)}</td>
                        <td className="c-green c-num">{fmt(row.seeTotal,    3)}</td>
                        <td className="c-green c-num">{fmt(row.embDirect)}</td>
                        <td className="c-green c-num">{fmt(row.embIndirect)}</td>
                        <td className="c-green c-num">{fmt(row.embTotal)}</td>
                        <td className="c-green c-center">{row.elecSrc}</td>
                        <td className="c-green c-num">{fmt(row.elecPerT, 3)}</td>
                        <td className="c-green c-center">{row.countryCode}</td>
                        <td className="c-white c-center">—</td>
                        <td className="c-white c-center">—</td>
                      </tr>
                    ))}

                    {proc.subRows.length === 0 && (
                      <tr>
                        <td colSpan={15} className="c-grey c-center" style={{ fontSize: 10 }}>
                          No data rows — check Form D and Form E entries
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>

      </div>
    </>
  );
};

export default Summary1Installation;